import { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core'
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
} from 'chart.js'
import { ThemeProvider } from 'next-themes'
import '@fontsource/nunito/400.css'
import '@fontsource/nunito/700.css'

import { Dialog, Flex } from 'app/ui'
import { GlobalSnackbar, Header, Sidebar } from 'app/components/app'

import { useUserStore } from 'app/stores/app/user'
import { urls } from 'app/utils/constants'
import { joinStyles } from 'app/utils/theme'
import { defaultPalette } from 'app/theme/defaultPalette.css'

import { Routes } from 'app/Routes'
import { RecoilRoot } from 'recoil'

ChartJS.register(
    BarElement,
    CategoryScale,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Tooltip,
)

const useStyles = makeStyles({
    content: {
        overflow: 'hidden',
        flex: 'auto',
        justifyContent: 'unset',
        alignItems: 'unset',
    },

    dialog: {
        justifyContent: 'unset',
        alignItems: 'unset',
    },

    main: {
        overflow: 'auto',
        flex: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },

    padding: {
        padding: '10px',
        paddingBottom: '5vh',
    },
})

const App = (): JSX.Element | null => {
    const classes = useStyles()
    const theme = useTheme()
    const isMoreThanLg = useMediaQuery(theme.breakpoints.up('lg'))

    const user = useUserStore((state) => state.user)
    const updateUser = useUserStore((state) => state.updateUser)

    const [showSidebar, setShowSidebar] = useState(isMoreThanLg)

    const { pathname } = window.location

    const emptyPageLayoutRoutes = [
        urls.auth.LOGIN,
        `${urls.auth.LOGIN}/`,

        urls.auth.IMPERSONATE,
        `${urls.auth.IMPERSONATE}/`,

        urls.auth.RESET_PASSWORD,
        `${urls.auth.RESET_PASSWORD}/`,
    ]

    const isLoggedPage = !emptyPageLayoutRoutes.includes(pathname)

    const toggleShowSidebar = () => {
        setShowSidebar(!showSidebar)
    }

    // Trigger the LG breakpoint change
    useEffect(() => {
        setShowSidebar(isMoreThanLg)
    }, [isMoreThanLg])

    // Trigger the page change
    useEffect(() => {
        if (!isMoreThanLg) {
            setShowSidebar(false)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname])

    // Initialise the data of the logged user
    useEffect(() => {
        if (isLoggedPage) {
            updateUser()
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateUser])

    // Wait the user data
    if (user.id === -1 && isLoggedPage) {
        return null
    }

    return (
        <>
            {/* @ts-expect-error Wait type */}
            <ThemeProvider
                attribute='class'
                defaultTheme='default'
                value={{
                    default: defaultPalette,
                }}
            >
                <RecoilRoot>
                    {/* Global snackbar */}
                    <GlobalSnackbar />

                    {/* Header */}
                    {!!isLoggedPage && (
                        <Header toggleShowSidebar={toggleShowSidebar} />
                    )}

                    <Flex className={classes.content}>
                        {/* Sidebar */}
                        {!!isLoggedPage && !!showSidebar && !!isMoreThanLg && (
                            <Sidebar />
                        )}

                        {!!isLoggedPage && !!showSidebar && !isMoreThanLg && (
                            <Dialog
                                className={classes.dialog}
                                onClose={() => {
                                    setShowSidebar(false)
                                }}
                                open
                            >
                                <Sidebar />
                            </Dialog>
                        )}

                        {/* Routes */}
                        <main
                            className={joinStyles(
                                classes.main,
                                isLoggedPage && classes.padding,
                            )}
                        >
                            <Routes />
                        </main>
                    </Flex>
                </RecoilRoot>
            </ThemeProvider>
        </>
    )
}

export default withRouter(App)
